import { useState, useCallback } from "react"

interface HoverLocation {
  warehouseId: string
  channel: string
}

export function useBidirectionalHover<T extends { id: string; name: string }>() {
  // bidirectional hover state for the matrix / table and bar charts
  // in estimated sales price and constraint avg price tooltips
  const [hoveredLocation, setHoveredLocation] = useState<HoverLocation | null>(null)

  const handleMatrixCellHover = useCallback(
    (rowIndex: number, colIndex: number, channels: T[], warehouses: T[]) => {
      const channel = channels[rowIndex]
      const warehouse = warehouses[colIndex]
      if (channel && warehouse) {
        setHoveredLocation({ warehouseId: warehouse.id, channel: channel.id })
      }
    },
    [],
  )

  const handleMatrixCellLeave = useCallback(() => {
    setHoveredLocation(null)
  }, [])

  const handleBarHover = useCallback((warehouseId: string, channel: string) => {
    setHoveredLocation({ warehouseId, channel })
  }, [])

  const handleBarLeave = useCallback(() => {
    setHoveredLocation(null)
  }, [])

  const getHoveredCell = useCallback(
    (channels: T[], warehouses: T[]) => {
      if (!hoveredLocation) return null
      return {
        rowIndex: channels.findIndex((ch) => ch.id === hoveredLocation.channel),
        colIndex: warehouses.findIndex((wh) => wh.id === hoveredLocation.warehouseId),
      }
    },
    [hoveredLocation],
  )

  return {
    hoveredLocation,
    handleMatrixCellHover,
    handleMatrixCellLeave,
    handleBarHover,
    handleBarLeave,
    getHoveredCell,
  }
}
